import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { API } from "../config/api";

/**
 * useSmartSearchResolver — Item 21-patch (#189 rollout to trackers).
 *
 * Routes free-text tracker search through the same server-side resolver
 * that SmartClientMatterSearch.jsx uses (`GET /api/smart-search`), instead
 * of each tracker running its own client-side .filter() text match.
 * "One resolver, everywhere" — see backend/hub/SEARCH_RESOLVER.md.
 *
 * This hook mirrors SmartClientMatterSearch's doSearch() contract exactly:
 *   - 300ms debounce
 *   - no request fired below 2 characters
 *   - GET `${API}/smart-search` with `q`, `limit`, and `matters_only` params
 *   - `withCredentials: true`
 *   - silent-fail to empty results on error (matches SmartClientMatterSearch)
 *
 * Trackers are row-list surfaces, not a single-pick autocomplete, so this
 * hook does NOT render a dropdown. Instead it exposes the resolved set of
 * matter references (+ client names) matching the free-text query so the
 * calling tracker can narrow its own already-loaded rows client-side —
 * preserving each tracker's existing columns/layout while the actual text
 * matching runs through the resolver.
 *
 * matters_only=true is used everywhere here because every tracker row is
 * matter-keyed (invoice/payment/deadline/pipeline all key off
 * matter_reference) — a bare contact-only hit can't narrow a matter list,
 * mirroring the documented `mattersOnly` use-case in
 * SmartClientMatterSearch.jsx ("the picked result must carry a
 * matter_reference").
 *
 * @param {string} query        Free-text search box value.
 * @param {object} [opts]
 * @param {number} [opts.limit] Passed through to /smart-search (default 8).
 * @returns {{
 *   matchedRefs: Set<string>,   // uppercased matter_reference values
 *   matchedClientNames: Set<string>, // lowercased client_name values
 *   results: Array,             // raw resolver results, for callers that want more
 *   searching: boolean,
 *   ready: boolean,             // false while query.length >= 2 and no response has landed yet
 * }}
 */
/**
 * BM-H3451 (2 Oct 2026). True when a matter reference contains the typed
 * text as a full or partial reference: "1539", "002.1539", "vec 002" and
 * "BL.VEC.002.1539" all find BL.VEC.002.1539. Case, dots and spaces are
 * ignored. Trackers already hold every row they display, so a reference
 * match is decided here on those rows rather than trusting the resolver's
 * capped, status-filtered result list to contain it.
 */
export function refMatchesQuery(ref, query) {
  const r = (ref || "").toUpperCase();
  const q = (query || "").trim().toUpperCase();
  if (!r || q.length < 2) return false;
  if (r.includes(q)) return true;
  const strip = (s) => s.replace(/[^A-Z0-9]/g, "");
  const qs = strip(q);
  return qs.length >= 2 && strip(r).includes(qs);
}

export function useSmartSearchResolver(query, opts = {}) {
  // BM-H3451: activeOnly is passed through to /smart-search. It defaults to
  // the endpoint's own default (true). The Pre-Open Pipeline passes false,
  // because every matter on it is pre-open and so "pending", which the
  // active filter excludes at the database.
  const { limit = 8, activeOnly = true } = opts;
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [ready, setReady] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const q = (query || "").trim();

    if (timerRef.current) clearTimeout(timerRef.current);

    if (q.length < 2) {
      // Empty / too-short query — preserve existing behaviour: show the
      // full list. No resolver call, no stale results held over.
      setResults([]);
      setSearching(false);
      setReady(true);
      return;
    }

    setReady(false);
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const resp = await axios.get(`${API}/smart-search`, {
          params: { q, limit, matters_only: true, active_only: activeOnly },
          withCredentials: true,
        });
        const data = resp.data || {};
        setResults(data.results || []);
      } catch {
        setResults([]);
      }
      setSearching(false);
      setReady(true);
    }, 300);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [query, limit, activeOnly]);

  const matchedRefs = new Set(
    results.map((r) => (r.matter_reference || "").toUpperCase()).filter(Boolean)
  );
  const matchedClientNames = new Set(
    results.map((r) => (r.client_name || "").toLowerCase()).filter(Boolean)
  );

  return { matchedRefs, matchedClientNames, results, searching, ready };
}

export default useSmartSearchResolver;
